import React, { useEffect, useState } from "react";
import {
  Bars3BottomRightIcon,
  CheckIcon,
  UserIcon,
  XMarkIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline"; // Import Tailwind CSS Icons
import IssueForm from "./IssueForm";
import "../CSS/TicketList.css";
import PageLoader from "./pageLoaders/PageLoader";

const ChatList = ({
  createNewChat,
  setTicketId,
  ticketId,
  conversations,
  setSelectedConversation,
  setView,
  isExpanded,
  loading,
  setLoading,
  widgetConfigs,
  setupDetails,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");

  useEffect(() => {
    const status = localStorage.getItem("dexkor-chat-widget-filter-status");
    if (status) setStatusFilter(status);
  }, []);

  const handleCreateNewChat = () => {
    setShowForm(true);
  };

  const handleFormSubmit = (formData) => {
    createNewChat(formData);
    setShowForm(false); // Close form after submitting
  };

  const handleFilterChange = (status) => {
    setStatusFilter(status);
    localStorage.setItem("dexkor-chat-widget-filter-status", status);
    setShowFilter(false);
  };

  const filteredConversations = conversations
    .filter((conversation) => {
      if (statusFilter === "All") return true;
      return conversation?.ticketId?.status === statusFilter;
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const getStatusCounts = () => {
    const counts = {
      All: conversations.length,
      open: 0,
      closed: 0,
      resolved: 0,
    };
    conversations.forEach((conversation) => {
      const status = conversation?.ticketId?.status;
      if (status in counts) {
        counts[status]++;
      }
    });
    return counts;
  };

  const statusCounts = getStatusCounts();

  const handleSelectTicket = (conversation) => {
    setSelectedConversation(conversation);
    setView("message");
  };

  return (
    <div>
      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-full max-w-md p-4 rounded-lg shadow-lg">
            <IssueForm
              onSubmit={handleFormSubmit}
              setShowForm={setShowForm}
              ticketId={ticketId}
              setTicketId={setTicketId}
              loading={loading}
              setLoading={setLoading}
              widgetConfigs={widgetConfigs}
              setupDetails={setupDetails}
            />
          </div>
        </div>
      )}

      <div className="mt-10">
        <div className="flex flex-col">
          <div className="flex items-end">
            <h2 className="text-lg ml-4 font-semibold text-gray-800 mb-2 mt-8 flex justify-center items-center z-10 gap-2">
              <FunnelIcon className="w-4 h-4" />
              Tickets:{" "}
              <span
                className={`cursor-pointer ${
                  statusFilter === "open" && "text-green-500"
                } ${statusFilter === "resolved" && "text-yellow-500"} ${
                  statusFilter === "closed" && "text-red-500"
                } ${statusFilter === "All" && "text-blue-800"} ml-1`}
                onClick={() => setShowFilter(!showFilter)}
              >
                {statusFilter}
              </span>
            </h2>
            <button
              onClick={handleCreateNewChat}
              className="ml-auto rounded-full text-white px-4 py-2  mb-2 text-sm"
              style={{
                background: setupDetails?.widgetCustomizations.primaryColor,
              }}
            >
              Create new ticket
            </button>
          </div>
          {showFilter && (
            <div className="bg-gray-100 p-2 rounded-md shadow-md absolute top-32 left-4 w-2/4 z-10">
              <p className="font-medium">Filter by status</p>
              <ul className="space-y-1">
                {["All", "open", "resolved", "closed"].map((status) => (
                  <li
                    key={status}
                    className={`cursor-pointer p-1 rounded-md ${
                      statusFilter !== status && "hover:bg-gray-200"
                    }`}
                    style={{
                      background:
                        statusFilter === status
                          ? `${
                              setupDetails?.widgetCustomizations.primaryColor ||
                              "#4C73DE"
                            }`
                          : "",
                      color: statusFilter === status ? "white" : "",
                    }}
                    onClick={() => handleFilterChange(status)}
                  >
                    {status} ({statusCounts[status]})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <hr className="h-0.5  bg-gray-300 mb-0"></hr>
        <div className="relative">
          <div
            className={`overflow-auto dexkor-scrollbar-hidden scrollbar-thin ${
              isExpanded ? "expand" : ""
            }`}
          >
            {loading ? (
              <PageLoader />
            ) : (
              <ul className="p-4 space-y-4">
                {filteredConversations.length > 0 ? (
                  filteredConversations.map((conversation, index) => (
                    <li
                      key={index}
                      style={{
                        "--hover-color":
                          setupDetails?.widgetCustomizations?.secondaryColor,
                      }}
                      className="dexkor-ticket-li border rounded-xl shadow hover:bg-gray-200"
                      onClick={() => handleSelectTicket(conversation)}
                    >
                      <div className="flex items-center justify-between px-2.5 pt-2">
                        <h5 className="font-bold">
                          SLA Breaching for Closed Tickets
                        </h5>
                        <CheckIcon
                          className="h-5 w-5 bg-green-500 text-white rounded-full p-1"
                          strokeWidth={3}
                        />
                      </div>
                      <p className="dexkor-ticket-number">
                        {conversation?.ticketId?.ticketNumber}
                      </p>

                      <div className="flex items-center">
                        <div className="flex items-center pl-2.5">
                          <UserIcon className="w-7 h-6 border rounded-lg bg-gray-300" />
                          <p className="dexkor-ticket-status">
                            {conversation?.ticketId?.status}
                          </p>
                        </div>

                        <p className="dexkor-ticket-timestamp ml-auto">
                          {new Date(conversation.createdAt).toLocaleString([], {
                            year: "numeric",
                            month: "short", // or "2-digit" for numeric month
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </p>
                      </div>
                    </li>
                  ))
                ) : (
                  <p>No ticket</p>
                )}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatList;
